import { makeAutoObservable, action, computed, runInAction } from '../../common/stores/mobx-config.js';
import { dateProcessor } from '../../gedcom/processors/dateProcessor.js';
import familyTownsStore from '../familyMap/stores/familyTownsStore.js';
import { storeEvents, EVENTS } from '../../common/stores/storeEvents.js';
import { logger } from '../../utils/debug/logger.js';
import { getBranchFromSosa } from '../../common/utils/branchUtils.js';
import { TIMELINE_CONFIG } from './timelineConfig.js';

class TimelineEventsStore {
    events = [];
    groupedEvents = {};
    filteredEvents = [];

    constructor() {
        makeAutoObservable(this, {
            addEvent: action,
            clearEvents: action,
            setEvents: action,
            setFilteredEvents: action,
            hasEvents: computed
        });

        // Le géocodage des lieux IA arrive APRÈS la construction de la
        // chronologie (débounce + réseau) : re-résoudre les noms de villes
        // des événements déjà chargés quand les coordonnées arrivent
        // ("toulon" → "Toulon (Var)").
        storeEvents.subscribe(EVENTS.TOWN.UPDATE_COMPLETE, () => this.refreshTownNames());
    }

    /**
     * Re-résout town/townDisplay des événements dont la clé de lieu vient
     * d'obtenir des données dans familyTownsStore (post-géocodage).
     */
    refreshTownNames = () => {
        let changed = 0;
        runInAction(() => {
            this.events = this.events.map(event => {
                if (!event.townKey) return event;
                const townData = familyTownsStore.getTownsData(event.townKey);
                if (!townData?.town) return event;
                const townDisplay = familyTownsStore.formatTownDisplay(townData);
                if (event.town === townData.town && event.townDisplay === townDisplay) return event;
                changed++;
                return { ...event, town: townData.town, townDisplay };
            });
            if (changed > 0) this._updateGroupedEvents();
        });
        if (changed > 0) {
            logger.timeline.events.info(`🌍 ${changed} événement(s) de la chronologie re-résolus après géocodage`);
        }
    };

    /**
     * Groups events by year range, then by event type within each range.
     * Pure native JS — no lodash dependency.
     */
    _groupEvents = (events, yearsGroup = TIMELINE_CONFIG.YEARS_GROUP) => {
        // Parse and filter valid dates
        const validEvents = [];
        for (const event of events) {
            const parsedDate = dateProcessor.parseDate(event.date);
            if (parsedDate.isValid) {
                event.parsedDate = parsedDate.date;
                validEvents.push(event);
            }
        }

        // Sort by date ascending
        validEvents.sort((a, b) => a.parsedDate.getTime() - b.parsedDate.getTime());

        // Group by year range, then by type
        const grouped = {};
        for (const event of validEvents) {
            const yearStart = Math.floor(event.parsedDate.getFullYear() / yearsGroup) * yearsGroup;
            const key = `01/01/${yearStart}`;

            if (!grouped[key]) grouped[key] = {};
            const byType = grouped[key];

            if (!byType[event.type]) byType[event.type] = [];
            byType[event.type].push(event);
        }

        return grouped;
    };

    get hasEvents() {
        return this.events.length > 0;
    }

    get eventsByType() {
        return (type) => this.events.filter(event => event.type === type);
    }

    addEvent = (event) => {
        // Phase 6 Cloud: Gérer les événements enrichis sans date
        if (!event.type || !event.name) {
            if (event.t && !event.type) {
                logger.timeline.events.error('❌ Événement compressé détecté - décompression échouée:', {
                    compressedType: event.t,
                    compressedDate: event.d,
                    compressedPlace: event.l,
                    allFields: Object.keys(event),
                    hasName: !!event.name,
                    suggestion: 'Vérifier getDecompressedEvents dans rootPersonStore.js'
                });
            } else {
                logger.timeline.events.error('❌ Format d\'événement invalide:', {
                    type: event.type,
                    date: event.date,
                    name: event.name,
                    townKey: event.townKey,
                    town: event.town,
                    allFields: Object.keys(event)
                });
            }
            return;
        }

        // Accepter les événements sans date et leur assigner une date par défaut
        if (!event.date) {
            event.date = 'Date inconnue';
        }

        if (event.eventId && this.events.some(e => e.eventId === event.eventId)) {
            logger.timeline.events.warn('⚠️ Événement déjà existant, ignoré');
            return;
        }

        // Phase 8.3: Gérer townKey au lieu de town
        let town = "lieu inconnu";
        let townDisplay = "lieu inconnu";

        if (event.townKey) {
            const townData = familyTownsStore.getTownsData(event.townKey);
            if (townData) {
                town = townData.town;
                townDisplay = familyTownsStore.formatTownDisplay(townData) || townData.town;
            } else {
                town = event.townKey;
                townDisplay = event.townKey;
                logger.timeline.events.warn(`❌ Timeline: Aucune donnée de ville pour "${event.townKey}"`);
            }
        } else if (event.town) {
            town = event.town;
            townDisplay = event.townDisplay || event.town;
        }

        const enrichedEvent = {
            ...event,
            town,
            townDisplay,
            age: event.age || null,
            spouse: event.spouse || '',
            sosa: event.sosa || null
        };

        runInAction(() => {
            this.events.push(enrichedEvent);
            this._updateGroupedEvents();
        });
    };

    setEvents = (events) => {
        runInAction(() => {
            this.events = events;
            this._updateGroupedEvents();
        });
    };

    clearEvents = () => {
        runInAction(() => {
            logger.timeline.events.info(`🧹 Timeline: clearEvents appelé - ${this.events.length} événements supprimés`);
            this.events = [];
            this.groupedEvents = {};
        });
    };

    _updateGroupedEvents = () => {
        this.groupedEvents = this._groupEvents(this.events);
    };

    /**
     * Détermine la branche et la génération à partir d'un numéro Sosa.
     * Utilise l'utilitaire partagé branchUtils.js.
     */
    _getAncestorBranchAndGeneration = (sosaNumber) => {
        return getBranchFromSosa(sosaNumber);
    };

    formatEvent = (event, eventType) => {
        const personLink = event.personId
            ? `<a href="#" class="person-link text-decoration-none fw-bold" data-person-id="${event.personId}" data-show-details="true">${event.name}</a>`
            : event.name;

        if (event.sosa) {
            const ancestorInfo = this._getAncestorBranchAndGeneration(event.sosa);
            const color = ancestorInfo.branch === 'paternal' ? 'darkblue' : 'deeppink';
            const spouseInfo = event.spouse ? ` with ${event.spouse}` : '';
            return `${personLink} (${event.date})${spouseInfo} at ${event.town} <span style="color: ${color}">(+ ${ancestorInfo.generation} gen.)</span>`;
        } else {
            return eventType === 'birth'
                ? `${personLink} (${event.date}) at ${event.town}`
                : eventType === 'death'
                ? `${personLink} (${event.date}) at the age of ${event.age} at ${event.town}`
                : `${personLink} (${event.date})${event.spouse ? ` with ${event.spouse}` : ''} at ${event.town}`;
        }
    };

    getAllEvents = () => this.events;
    getGroupedEvents = () => this.groupedEvents;

    setFilteredEvents = (events) => {
        runInAction(() => {
            this.filteredEvents = events;
            this._updateGroupedFilteredEvents();
        });
    };

    _updateGroupedFilteredEvents = () => {
        this.groupedEvents = this._groupEvents(this.filteredEvents);
    };

    getFilteredEvents = () => this.filteredEvents;
}

const timelineEventsStore = new TimelineEventsStore();
export default timelineEventsStore;
